import { router } from "@inertiajs/react"
import ViewToggle, { type ViewMode } from "./view-toggle"

const selectClass =
  "appearance-none bg-[length:16px] bg-[right_8px_center] bg-no-repeat bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22%239ca3af%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.22%208.22a.75.75%200%200%201%201.06%200L10%2011.94l3.72-3.72a.75.75%200%201%201%201.06%201.06l-4.25%204.25a.75.75%200%200%201-1.06%200L5.22%209.28a.75.75%200%200%201%200-1.06z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')]"

interface FilterConfig {
  label: string
  param: string
  options: { value: string; label: string }[]
}

interface SortOption {
  value: string
  label: string
}

interface IndexToolbarProps {
  filters: FilterConfig[]
  sortOptions: SortOption[]
  currentFilters: Record<string, string | undefined>
  currentSort: string
  currentDirection: string
  view?: ViewMode
  onViewChange?: (view: ViewMode) => void
  baseUrl: string
  count: number
  noun: string
}

export default function IndexToolbar({
  filters,
  sortOptions,
  currentFilters,
  currentSort,
  currentDirection,
  view,
  onViewChange,
  baseUrl,
  count,
  noun,
}: IndexToolbarProps) {
  function navigate(updates: Record<string, string | undefined>) {
    const params: Record<string, string> = {}

    // Carry forward existing filters
    for (const [key, val] of Object.entries(currentFilters)) {
      if (val) params[key] = val
    }
    if (currentSort) params.sort = currentSort
    if (currentDirection) params.direction = currentDirection

    // Apply updates, removing empty values
    for (const [key, val] of Object.entries(updates)) {
      if (val) {
        params[key] = val
      } else {
        delete params[key]
      }
    }

    // Reset to page 1 on filter/sort change
    delete params.page

    router.get(baseUrl, params, { preserveState: true })
  }

  function toggleDirection() {
    navigate({ direction: currentDirection === "asc" ? "desc" : "asc" })
  }

  const pluralNoun = count === 1 ? noun : `${noun}s`

  return (
    <div className="mb-6 flex flex-wrap items-center gap-3">
      {/* Filters */}
      {filters.map((filter) => (
        <select
          key={filter.param}
          value={currentFilters[filter.param] || ""}
          onChange={(e) => navigate({ [filter.param]: e.target.value || undefined })}
          className={`h-9 rounded-lg border bg-white pl-3 pr-8 text-sm transition-colors ${selectClass} ${
            currentFilters[filter.param]
              ? "border-red/40 font-medium text-charcoal"
              : "border-stone/40 text-warm-grey"
          }`}
        >
          <option value="">{filter.label}</option>
          {filter.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ))}

      {/* Sort */}
      <div className="flex items-center">
        <select
          value={currentSort || "created_at"}
          onChange={(e) => navigate({ sort: e.target.value })}
          className={`h-9 rounded-l-lg border border-r-0 border-stone/40 bg-white pl-3 pr-8 text-sm text-warm-grey ${selectClass}`}
        >
          {sortOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button
          onClick={toggleDirection}
          className="flex h-9 items-center justify-center rounded-r-lg border border-stone/40 bg-white px-2 text-warm-grey transition-colors hover:text-charcoal"
          aria-label={currentDirection === "asc" ? "Ascending" : "Descending"}
        >
          <svg
            className={`size-3.5 transition-transform ${currentDirection === "asc" ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
          </svg>
        </button>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Count */}
      <span className="text-sm text-warm-grey">
        {count} {pluralNoun}
      </span>

      {/* View toggle */}
      {view !== undefined && onViewChange && (
        <ViewToggle view={view} onChange={onViewChange} />
      )}
    </div>
  )
}
